import { useState } from 'react'
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  CalendarClock,
  ExternalLink,
  Gavel,
  Lock,
  ShieldCheck,
  Sparkles,
  Trophy,
  Upload,
  Users,
} from 'lucide-react'

import { ShareButton } from '@/components/share'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input, Textarea } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { ContestEntryBadge, ContestStatusBadge } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useSession } from '@/features/auth/api'
import {
  useAwardContest,
  useContest,
  useFundContest,
  useJudgingMode,
  useShortlistEntry,
  useSubmitEntry,
} from '@/features/contest/api'
import { ApiError } from '@/lib/api'
import { formatDate, formatDateTime, formatRupiah } from '@/lib/format'
import type { ContestEntry } from '@/lib/types'

const route = getRouteApi('/public/sayembara/$contestId')

export function ContestDetailPage() {
  const { contestId } = route.useParams()
  const { data, isLoading } = useContest(contestId)
  const { data: session } = useSession()
  const navigate = useNavigate()

  const [entryOpen, setEntryOpen] = useState(false)
  const [awardTarget, setAwardTarget] = useState<ContestEntry | null>(null)

  const fund = useFundContest(contestId)
  const judging = useJudgingMode(contestId)
  const shortlist = useShortlistEntry(contestId)
  const toast = useToast()

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
        <Skeleton className="h-80 rounded-2xl" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-20 sm:px-6">
        <EmptyState
          className="card"
          title="Sayembara tidak ditemukan"
          description="Sayembara mungkin sudah dibatalkan penyelenggaranya."
          action={
            <Link to="/sayembara">
              <Button variant="outline">Kembali ke daftar sayembara</Button>
            </Link>
          }
        />
      </div>
    )
  }

  const { contest, is_organizer: isOrganizer } = data
  const entries = contest.entries ?? []
  const canEnter = contest.status === 'open' && !isOrganizer

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6">
      <Link
        to="/sayembara"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted transition hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        Sayembara
      </Link>

      <div className="mt-4 grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <Card>
            <CardBody>
              <div className="flex flex-wrap items-center gap-2">
                <ContestStatusBadge status={contest.status} />
                <Badge tone="brand">{contest.category}</Badge>
                {contest.funded ? (
                  <Badge tone="success" icon={<Lock className="size-3.5" />}>
                    Hadiah terkunci
                  </Badge>
                ) : (
                  <Badge tone="warning">Hadiah belum disetor</Badge>
                )}
              </div>

              <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
                {contest.title}
              </h1>

              <p className="mt-2 text-sm text-muted">
                Diselenggarakan {contest.client.full_name}
                {contest.closes_at ? ` · ditutup ${formatDate(contest.closes_at)}` : ''}
              </p>

              <div className="mt-4">
                <ShareButton
                  title={contest.title}
                  text={`Sayembara di skava.id: ${contest.title}`}
                  label="Bagikan sayembara"
                />
              </div>

              <p className="mt-5 whitespace-pre-line leading-relaxed text-muted">{contest.description}</p>

              {contest.skills.length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {contest.skills.map((skill) => (
                    <span key={skill} className="rounded-full bg-sunken px-3 py-1 text-sm font-medium text-muted">
                      {skill}
                    </span>
                  ))}
                </div>
              ) : null}
            </CardBody>
          </Card>

          {isOrganizer ? (
            <Card>
              <CardHeader
                title={`Karya masuk (${entries.length})`}
                description="Karya peserta hanya terlihat oleh Anda sampai pemenang diumumkan."
              />

              {entries.length === 0 ? (
                <EmptyState
                  icon={<Users className="size-6" />}
                  title="Belum ada karya"
                  description="Karya peserta akan muncul di sini beserta catatannya."
                />
              ) : (
                <ul className="divide-y divide-[var(--border)]">
                  {entries.map((entry) => (
                    <li key={entry.id} className="px-5 py-4">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex min-w-0 gap-3">
                          <Avatar name={entry.participant_name} />
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-bold text-ink">{entry.title || 'Tanpa judul'}</p>
                              <ContestEntryBadge state={entry.state} />
                            </div>
                            <p className="text-sm text-muted">{entry.participant_name}</p>
                            <EntryLinks files={entry.files} />
                            {entry.note ? (
                              <p className="mt-2 whitespace-pre-line text-sm text-muted">{entry.note}</p>
                            ) : null}
                            <p className="mt-1 text-xs text-subtle">{formatDateTime(entry.created_at)}</p>
                          </div>
                        </div>

                        {contest.status !== 'awarded' && entry.state !== 'rejected' ? (
                          <div className="flex shrink-0 gap-2">
                            {entry.state === 'submitted' ? (
                              <Button
                                size="sm"
                                variant="outline"
                                loading={shortlist.isPending}
                                onClick={() =>
                                  shortlist.mutate(entry.id, {
                                    onSuccess: () => toast.success('Karya masuk seleksi'),
                                  })
                                }
                              >
                                Masukkan seleksi
                              </Button>
                            ) : null}
                            <Button size="sm" onClick={() => setAwardTarget(entry)}>
                              <Trophy className="size-4" />
                              Jadikan pemenang
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          ) : contest.status === 'awarded' && entries.length > 0 ? (
            <Card>
              <CardHeader title="Karya pemenang" />
              <CardBody>
                {entries.map((entry) => (
                  <div key={entry.id} className="flex gap-3">
                    <Avatar name={entry.participant_name} />
                    <div>
                      <p className="font-bold text-ink">{entry.title}</p>
                      <p className="text-sm text-muted">{entry.participant_name}</p>
                      <EntryLinks files={entry.files} />
                      {entry.note ? <p className="mt-2 text-sm text-muted">{entry.note}</p> : null}
                    </div>
                  </div>
                ))}
              </CardBody>
            </Card>
          ) : null}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24 lg:h-fit">
          <Card>
            <CardBody className="space-y-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-subtle">Hadiah</p>
                <p className="mt-1 text-3xl font-extrabold tracking-tight text-ink">
                  {formatRupiah(contest.prize_amount)}
                </p>
                <p className="mt-1 text-sm text-muted">
                  Pemenang menerima {formatRupiah(contest.net_to_winner)} setelah komisi platform{' '}
                  {contest.platform_fee_percent}%.
                </p>
              </div>

              <dl className="space-y-2 border-t border-line pt-4 text-sm">
                <div className="flex justify-between">
                  <dt className="text-muted">Karya masuk</dt>
                  <dd className="font-semibold text-ink">{contest.entry_count}</dd>
                </div>
                {contest.closes_at ? (
                  <div className="flex justify-between">
                    <dt className="text-muted">Tenggat</dt>
                    <dd className="font-semibold text-ink">{formatDate(contest.closes_at)}</dd>
                  </div>
                ) : null}
              </dl>

              {isOrganizer ? (
                <div className="space-y-2">
                  {!contest.funded ? (
                    <Button
                      fullWidth
                      size="lg"
                      loading={fund.isPending}
                      onClick={() =>
                        fund.mutate(undefined, {
                          onSuccess: () => toast.success('Hadiah terkunci', 'Sayembara Anda kini tayang.'),
                          onError: (error) =>
                            toast.error('Gagal menyetor hadiah', error instanceof ApiError ? error.message : undefined),
                        })
                      }
                    >
                      <ShieldCheck className="size-4.5" />
                      Setor hadiah & tayangkan
                    </Button>
                  ) : contest.status === 'open' ? (
                    <Button
                      fullWidth
                      variant="outline"
                      loading={judging.isPending}
                      onClick={() =>
                        judging.mutate(undefined, {
                          onSuccess: () => toast.info('Sayembara masuk tahap penilaian'),
                          onError: (error) =>
                            toast.error('Gagal menutup pengiriman', error instanceof ApiError ? error.message : undefined),
                        })
                      }
                    >
                      Tutup pengiriman & nilai
                    </Button>
                  ) : null}
                </div>
              ) : canEnter ? (
                <Button
                  fullWidth
                  size="lg"
                  onClick={() => {
                    if (!session) {
                      navigate({ to: '/masuk', search: { redirect: `/sayembara/${contestId}` } })
                      return
                    }
                    setEntryOpen(true)
                  }}
                >
                  <Upload className="size-4.5" />
                  {contest.my_entry_id ? 'Kirim karya lagi' : 'Kirim karya'}
                </Button>
              ) : (
                <p className="rounded-xl bg-sunken p-4 text-sm text-muted">
                  {contest.status === 'awarded'
                    ? 'Sayembara ini sudah menemukan pemenangnya.'
                    : 'Pengiriman karya sudah ditutup.'}
                </p>
              )}

              {contest.my_entry_id ? (
                <div className="rounded-xl bg-success-soft p-3.5 text-sm text-ink">
                  Karya Anda sudah terkirim. Penyelenggara akan mengumumkan pemenang setelah penilaian.
                </div>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardBody className="flex gap-3 text-sm text-muted">
              <Sparkles className="mt-0.5 size-4 shrink-0 text-brand" />
              <p>
                Hadiah sudah disetor ke rekening penampungan sebelum sayembara tayang. Anda mempertaruhkan waktu,
                bukan kepastian hadiahnya.
              </p>
            </CardBody>
          </Card>

          {contest.closes_at ? (
            <Card>
              <CardBody className="flex gap-3 text-sm text-muted">
                <CalendarClock className="mt-0.5 size-4 shrink-0 text-brand" />
                <p>Kirim karya sebelum {formatDate(contest.closes_at)}. Setelah itu penilaian dimulai.</p>
              </CardBody>
            </Card>
          ) : null}
        </aside>
      </div>

      <EntryDialog contestId={contestId} open={entryOpen} onClose={() => setEntryOpen(false)} />
      <AwardDialog contestId={contestId} entry={awardTarget} onClose={() => setAwardTarget(null)} />
    </div>
  )
}

/**
 * EntryLinks menampilkan tautan karya peserta.
 *
 * Skema tautan sudah dibatasi http dan https di sisi peladen sebelum
 * disimpan; pemeriksaan diulang di sini karena data lama — yang masuk
 * sebelum pembatasan itu ada — tetap tersimpan apa adanya di basis data.
 */
function EntryLinks({ files }: { files: ContestEntry['files'] }) {
  const links = (files ?? []).filter((file) => {
    if (!file.url) return false
    try {
      const parsed = new URL(file.url)
      return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch {
      return false
    }
  })

  if (links.length === 0) return null

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {links.map((file) => (
        <a
          key={file.url}
          href={file.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mono-label inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-[11px] text-brand transition hover:border-brand"
        >
          <ExternalLink className="size-3.5" />
          {file.name || 'Buka karya'}
        </a>
      ))}
    </div>
  )
}

function EntryDialog({
  contestId,
  open,
  onClose,
}: {
  contestId: string
  open: boolean
  onClose: () => void
}) {
  const submitEntry = useSubmitEntry(contestId)
  const toast = useToast()
  const [values, setValues] = useState({ title: '', note: '', url: '' })

  // Peramban sudah memeriksa type=url, tetapi hanya saat formulir dikirim —
  // dan dialog ini tidak memakai formulir. Pemeriksaannya karena itu
  // dikerjakan sendiri, sehingga pesan galat muncul saat mengetik, bukan
  // setelah permintaan ditolak peladen.
  const urlError = (() => {
    const trimmed = values.url.trim()
    if (!trimmed) return undefined
    try {
      const parsed = new URL(trimmed)
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return 'Tautan harus diawali http:// atau https://.'
      }
      return undefined
    } catch {
      return 'Tautan belum lengkap. Contoh: https://drive.google.com/...'
    }
  })()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Kirim karya"
      description="Sertakan tautan karya Anda dan jelaskan gagasannya seringkas mungkin — penyelenggara membaca banyak kiriman."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            loading={submitEntry.isPending}
            disabled={values.title.trim().length < 3 || Boolean(urlError)}
            onClick={() =>
              submitEntry.mutate(values, {
                onSuccess: () => {
                  toast.success('Karya terkirim', 'Anda akan diberi tahu ketika pemenang ditetapkan.')
                  setValues({ title: '', note: '', url: '' })
                  onClose()
                },
                onError: (error) =>
                  toast.error('Gagal mengirim karya', error instanceof ApiError ? error.message : undefined),
              })
            }
          >
            Kirim karya
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Input
          label="Judul karya"
          value={values.title}
          onChange={(event) => setValues((current) => ({ ...current, title: event.target.value }))}
          placeholder="Misalnya: Konsep 'Biji Nusantara'"
        />

        <Input
          label="Tautan karya"
          type="url"
          inputMode="url"
          value={values.url}
          onChange={(event) => setValues((current) => ({ ...current, url: event.target.value }))}
          placeholder="https://drive.google.com/..."
          hint="Google Drive, Figma, Behance, atau tempat lain. Pastikan aksesnya terbuka untuk penyelenggara."
          error={urlError}
        />

        <Textarea
          label="Penjelasan"
          rows={5}
          value={values.note}
          onChange={(event) => setValues((current) => ({ ...current, note: event.target.value }))}
          placeholder="Gagasan di balik karya, pilihan warna dan bentuk, serta bagaimana penerapannya."
        />
      </div>
    </Dialog>
  )
}

function AwardDialog({
  contestId,
  entry,
  onClose,
}: {
  contestId: string
  entry: ContestEntry | null
  onClose: () => void
}) {
  const award = useAwardContest(contestId)
  const toast = useToast()

  return (
    <Dialog
      open={entry !== null}
      onClose={onClose}
      size="sm"
      title="Tetapkan pemenang"
      description={entry ? `${entry.title} — ${entry.participant_name}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            loading={award.isPending}
            onClick={() => {
              if (!entry) return
              award.mutate(entry.id, {
                onSuccess: (data) => {
                  toast.success(
                    'Pemenang ditetapkan',
                    `${formatRupiah(data.winner.net_to_winner)} masuk ke saldo ${data.winner.participant}.`,
                  )
                  onClose()
                },
                onError: (error) =>
                  toast.error('Gagal menetapkan pemenang', error instanceof ApiError ? error.message : undefined),
              })
            }}
          >
            <Gavel className="size-4" />
            Tetapkan &amp; bayar hadiah
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-muted">
          Hadiah langsung dibayarkan dari rekening penampungan ke saldo pemenang, dan karya peserta lain
          otomatis ditandai tidak terpilih. Keputusan ini tidak dapat dibatalkan.
        </p>
        <p className="text-xs leading-relaxed text-subtle">
          Seluruh peserta menerima pemberitahuan bahwa sayembara sudah diputuskan.
        </p>
      </div>
    </Dialog>
  )
}
